
import { useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge';
import { CancelOrderDialog } from '@/components/orders/CancelOrderDialog';
import { Order, OrderStatus } from '@/types/orders';
import { useOrderStore } from '@/store/orderStore';
import { updateOrderStatus as updateOrderStatusService } from '@/services/orders';
import { useToast } from '@/hooks/use-toast';
import { OrderItemsManagement } from './OrderItemsManagement';
import { useIsMobile } from '@/hooks/use-mobile';
import { X } from 'lucide-react';

interface OrderDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  viewOnly?: boolean; // Added viewOnly prop to control edit functionality
}

export function OrderDetailsDialog({
  open,
  onOpenChange,
  order,
  viewOnly = false, // Default to false for backward compatibility
}: OrderDetailsDialogProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const isMobile = useIsMobile();

  const { cancelOrder, updateOrder: updateOrderInStore } = useOrderStore();
  const { toast } = useToast();

  // Reset status when dialog opens with different order
  if (open && order && status !== order.status) {
    setStatus(order.status);
  }

  if (!order) {
    return null;
  }

  const formattedDate = format(
    new Date(order.createdAt),
    'MMM dd, yyyy h:mm a'
  );
  const isOrderCancellable =
    !viewOnly && order.status !== 'delivered' && order.status !== 'cancelled';

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (newStatus === status || viewOnly) return;

    setIsSubmitting(true);

    try {
      const result = await updateOrderStatusService(order.id, newStatus, order.status);
      if (result.success) {
        // Update the order in the store
        updateOrderInStore({ ...order, status: newStatus });
        setStatus(newStatus);

        toast({
          title: 'Status updated',
          description: `Order status changed to ${newStatus}`,
        });
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update order status',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update order status',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelOrder = async (orderId: string, reason: string) => {
    if (viewOnly) return;
    
    await cancelOrder(orderId, reason);
    setStatus('cancelled');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={isMobile ? "max-w-[95%] p-3 sm:p-6 w-full  sm:h-auto overflow-auto rounded-lg" : "max-w-md md:max-w-xl"}>
          <DialogHeader className="space-y-1">
            {isMobile && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute top-2 right-2 h-8 w-8 p-0" 
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <DialogTitle className="text-lg sm:text-xl flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <span>{viewOnly ? t('orderDetails') : t('manageOrder')}</span>
              <OrderStatusBadge status={order.status} />
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {t('order')} #{order.id.slice(0, 8)} - {formattedDate}
            </DialogDescription>
          </DialogHeader>

          <div className='grid gap-3 sm:gap-4 py-2 '>
            {/* Only show status dropdown in edit mode */}
            {!viewOnly && (
              <div>
                <Select
                  value={status}
                  onValueChange={(value) =>
                    handleStatusChange(value as OrderStatus)
                  }
                  disabled={
                    isSubmitting ||
                    order.status === 'cancelled' ||
                    order.status === 'delivered'
                  }>
                  <SelectTrigger className='w-full mt-1 h-8 sm:h-10 text-sm sm:text-base'>
                    <SelectValue placeholder={t('selectStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='pending'>{t('pending')}</SelectItem>
                    <SelectItem value='preparing'>{t('preparing')}</SelectItem>
                    <SelectItem value='ready'>{t('ready')}</SelectItem>
                    <SelectItem value='delivered'>{t('delivered')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <OrderItemsManagement
              items={order.items}
              orderId={order.id}
              tableNumber={order.tableNumber}
              tableName={order.tableName}
              status={order.status}
              specialInstructions={order.specialInstructions}
              viewOnly={viewOnly}
            />
          </div>

          <DialogFooter className='gap-2 mt-3 sm:mt-0'>
            {!isMobile && (
              <Button variant='outline' onClick={() => onOpenChange(false)} className="text-xs sm:text-sm h-8 sm:h-10">
                {t('close')}
              </Button>
            )}
            {isOrderCancellable && !viewOnly && (
              <Button
                variant='destructive'
                onClick={() => setShowCancelDialog(true)}
                className="text-xs sm:text-sm h-8 sm:h-10 w-full sm:w-auto">
                {t('cancelOrder')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!viewOnly && (
        <CancelOrderDialog
          open={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          orderId={order.id}
          onCancel={handleCancelOrder}
        />
      )}
    </>
  );
}
